import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus, AlertTriangle } from 'lucide-react'
import { Button } from '@components/common'
import WorkflowStepCard from './WorkflowStepCard'

import './WorkflowStepBuilder.scss'

/**
 * Generate a unique ID for new steps
 */
const generateStepId = () => `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

/**
 * WorkflowStepBuilder Component
 *
 * A drag-and-drop builder for creating workflow steps.
 * Allows adding, removing, reordering, and editing steps.
 */
const WorkflowStepBuilder = ({
	steps = [],
	users = [],
	onChange,
	disabled = false,
	maxSteps = 10,
}) => {
	const [activeId, setActiveId] = useState(null)

	// DnD sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	)

	// Handle drag start
	const handleDragStart = useCallback((event) => {
		setActiveId(event.active.id)
	}, [])

	// Handle drag end
	const handleDragEnd = useCallback(
		(event) => {
			const { active, over } = event
			setActiveId(null)

			if (over && active.id !== over.id) {
				const oldIndex = steps.findIndex((s) => s.id === active.id)
				const newIndex = steps.findIndex((s) => s.id === over.id)
				const newSteps = arrayMove(steps, oldIndex, newIndex)
				onChange(newSteps)
			}
		},
		[steps, onChange]
	)

	// Handle drag cancel
	const handleDragCancel = useCallback(() => {
		setActiveId(null)
	}, [])

	// Add new step
	const handleAddStep = useCallback(() => {
		if (steps.length >= maxSteps) return

		const newStep = {
			id: generateStepId(),
			name: '',
			role: '',
			approver_id: null,
			sla_hours: 24,
			evidence_required: 0,
		}

		onChange([...steps, newStep])
	}, [steps, maxSteps, onChange])

	// Update step
	const handleStepChange = useCallback(
		(stepId, updatedStep) => {
			const newSteps = steps.map((s) => (s.id === stepId ? updatedStep : s))
			onChange(newSteps)
		},
		[steps, onChange]
	)

	// Remove step
	const handleRemoveStep = useCallback(
		(stepId) => {
			const newSteps = steps.filter((s) => s.id !== stepId)
			onChange(newSteps)
		},
		[steps, onChange]
	)

	// Check for validation warnings (duplicate roles without specific approvers)
	const getWarnings = useCallback(() => {
		const warnings = []

		// Check for steps without roles
		const stepsWithoutRole = steps.filter((s) => !s.role)
		if (stepsWithoutRole.length > 0) {
			warnings.push('Some steps are missing role selection')
		}

		// Check for duplicate role + approver combinations
		const combinations = new Map()
		steps.forEach((step, index) => {
			if (step.role) {
				const key = `${step.role}_${step.approver_id || 'any'}`
				if (combinations.has(key)) {
					warnings.push(
						`Step ${index + 1} has the same role and approver as Step ${combinations.get(key) + 1}`
					)
				} else {
					combinations.set(key, index)
				}
			}
		})

		return warnings
	}, [steps])

	const warnings = getWarnings()
	const stepIds = steps.map((s) => s.id)

	return (
		<div className="workflow-step-builder">
			{/* Header */}
			<div className="workflow-step-builder__header">
				<div className="workflow-step-builder__title">
					<h3>Workflow Steps</h3>
					<span className="workflow-step-builder__count">
						{steps.length} / {maxSteps} steps
					</span>
				</div>
				{!disabled && (
					<Button
						variant="outline"
						size="small"
						onClick={handleAddStep}
						disabled={steps.length >= maxSteps}
					>
						<Plus size={16} />
						Add Step
					</Button>
				)}
			</div>

			{/* Warnings */}
			{warnings.length > 0 && (
				<div className="workflow-step-builder__warnings">
					{warnings.map((warning, index) => (
						<div key={index} className="workflow-step-builder__warning">
							<AlertTriangle size={16} />
							<span>{warning}</span>
						</div>
					))}
				</div>
			)}

			{/* Steps List */}
			<div className="workflow-step-builder__list">
				{steps.length === 0 ? (
					<div className="workflow-step-builder__empty">
						<p>No steps added yet. Add your first step to define the approval workflow.</p>
						{!disabled && (
							<Button variant="primary" onClick={handleAddStep}>
								<Plus size={18} />
								Add First Step
							</Button>
						)}
					</div>
				) : (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragStart={handleDragStart}
						onDragEnd={handleDragEnd}
						onDragCancel={handleDragCancel}
					>
						<SortableContext items={stepIds} strategy={verticalListSortingStrategy}>
							{steps.map((step, index) => (
								<WorkflowStepCard
									key={step.id}
									step={step}
									stepNumber={index + 1}
									users={users}
									onChange={handleStepChange}
									onRemove={handleRemoveStep}
									disabled={disabled}
									isDragging={activeId === step.id}
								/>
							))}
						</SortableContext>
					</DndContext>
				)}
			</div>

			{/* Help Text */}
			<div className="workflow-step-builder__help">
				<p>
					<strong>Tip:</strong> Drag steps to reorder them. Each step defines an approval stage in the workflow.
					The order determines the sequence of approvals.
				</p>
			</div>
		</div>
	)
}

WorkflowStepBuilder.propTypes = {
	steps: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string,
			role: PropTypes.string,
			approver_id: PropTypes.number,
			sla_hours: PropTypes.number,
			evidence_required: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
		})
	),
	users: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	maxSteps: PropTypes.number,
}

export default WorkflowStepBuilder
